"use client";

import { Company } from "@entities/employer";
import { MapPinIcon } from "@heroicons/react/24/solid";
import { TCompanyDetailPageMeta } from "@shared/cms";
import { DetailItem, GoogleMapEmbed, HtmlParser, PageShareButtons } from "@shared/ui";
import { formatDate, imageFallback } from "@shared/utils";
import Image from "next/image";

export function CompanyInfoSection({ company, meta }: { company: Company; meta: TCompanyDetailPageMeta }) {
  return (
    <div className="container space-y-10">
      <div>
        <div className="mt-6 sm:mt-5 flex-1 flex flex-wrap justify-between md:items-center md:space-x-5">
          <div className="w-12 h-12 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-gray-200">
            <Image
              src={company.logoUri || "/blank-white.webp"}
              alt={company.name}
              width={78}
              height={78}
              className="w-full h-full object-contain"
              onError={imageFallback()}
            />
          </div>
          <div className="order-2 flex-1 space-y-1 min-w-full md:min-w-0 mt-4 md:mt-0">
            <div className="flex items-center space-x-2">
              <h1 className="text-lg sm:text-xl font-bold text-gray-700">{company.name}</h1>
            </div>
            <div className="text-base font-medium text-gray-700">{company.industry?.name || company.industryName}</div>
            <div className="text-sm text-gray-500">
              {meta?.registerSinceLabel || "Terdaftar sejak"} {formatDate(company.createdAt || company.employer?.createdAt, "dd MMMM yyyy")}
            </div>
          </div>
        </div>
        <PageShareButtons />
        <div className="mt-5 sm:mt-6 text-sm text-grey-500 !leading-[160%]">
          <HtmlParser data={company.about || "Perusahaan ini telah terdaftar di KARIRHUB"} />
        </div>
        <div className="mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
          <DetailItem label={meta.phoneLabel || "Kontak perusahaan"} value={company.phone} />
          <DetailItem label={meta.emailLabel || "Email perusahaan"} value={company.email} />
          <DetailItem label={meta.websiteLabel || "Website perusahaan"} value={company.website} />
        </div>
      </div>
      <div>
        <h2 className="sm:text-lg font-bold text-grey-700 flex-1">{meta.addressLabel}</h2>
        <div className="mt-4">
          <div className="bg-white border border-gray-200 rounded-xl">
            {company.geoloc && company.geoloc.length === 2 && <GoogleMapEmbed lat={company.geoloc[0]} lng={company.geoloc[1]} />}
            {company.locationPoint && <GoogleMapEmbed lat={company.locationPoint.latitude} lng={company.locationPoint.longitude} />}
            <div className="p-4">
              {company.city && (
                <h3 className="flex items-center gap-2 font-bold text-base">
                  <MapPinIcon className="size-6 min-w-6 text-primary" />
                  <span className="flex-1">{company.city?.name}</span>
                </h3>
              )}
              {company.address && <p className="text-sm text-gray-500 mt-2">{company.address}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
